import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, LOGO_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const TERMS_TEXT = `TERMS AND CONDITIONS OF SALE — J&L INDUSTRIAL SUPPLIES

1. DEFINITIONS
"Company" means J&L Industrial Supplies. "Customer" means the person, firm, company or other organisation placing an order. "Goods" means any products, materials or items supplied by the Company. "Contract" means any contract between the Company and the Customer for the supply of Goods.

2. APPLICATION OF TERMS
These Terms and Conditions apply to all Contracts to the exclusion of all other terms including any terms the Customer purports to apply. No variation shall be binding unless agreed in writing by an authorised representative of the Company.

3. CREDIT ACCOUNTS
Credit accounts are granted at the Company's sole discretion and subject to satisfactory credit checks and trade references. The Company reserves the right to withdraw credit facilities at any time without notice. Credit limits will be notified in writing.

4. PAYMENT TERMS
Unless otherwise agreed in writing, payment is due within 30 days from the invoice date (Net 30). The Company reserves the right to charge interest on overdue accounts at 8% per annum above the Bank of England base rate, pursuant to the Late Payment of Commercial Debts (Interest) Act 1998, together with reasonable debt recovery costs.

5. PRICES AND VAT
All prices are exclusive of VAT unless stated otherwise. VAT is charged at the applicable statutory rate. Prices are subject to change without notice. Orders will be invoiced at the price applicable at the date of despatch.

6. DELIVERY
The Company will use reasonable endeavours to meet delivery dates, but time shall not be of the essence. Risk passes to the Customer upon delivery. The Company accepts no liability for losses caused by late delivery.

7. TITLE AND RISK
Title to the Goods shall not pass to the Customer until the Company has received payment in full for all Goods delivered. Until title passes, the Customer shall hold the Goods as the Company's bailee and keep them separate, stored, and insured.

8. RETURNS AND DAMAGED GOODS
The Customer must inspect Goods upon delivery and notify the Company of any shortages, damage or discrepancies within 3 working days. No returns will be accepted without prior written authorisation. Goods must be returned in original undamaged condition.

9. WARRANTIES AND LIABILITY
The Company warrants Goods will conform to specification at delivery. The Company's total liability shall not exceed the price paid for the relevant Goods. The Company shall not be liable for indirect, consequential, special or economic loss.

10. FORCE MAJEURE
The Company shall not be liable for delay or failure caused by circumstances beyond its reasonable control including acts of God, government actions, floods, fire, strikes, or supplier failures.

11. DATA PROTECTION
The Company will process personal data in accordance with the UK GDPR and Data Protection Act 2018, solely for managing the credit account and supply of Goods.

12. INSOLVENCY
If the Customer becomes insolvent, enters administration or liquidation, or makes any voluntary arrangement with creditors, the Company may cancel any undelivered order and all outstanding invoices become immediately due and payable.

13. GOVERNING LAW
These Terms and Conditions are governed by the laws of England and Wales. All disputes are subject to the exclusive jurisdiction of the courts of England and Wales.

14. ENTIRE AGREEMENT
These Terms and Conditions constitute the entire agreement between the parties and supersede all previous agreements, negotiations, representations and understandings.

DECLARATION: By signing this form, the Customer confirms that all information provided is true and accurate, and that they have read, understood and agreed to be bound by these Terms and Conditions in their entirety.`;

const TRADING_STYLES = [
  "Sole Trader",
  "Private Limited Company (Ltd)",
  "Partnership",
  "Limited Liability Partnership (LLP)",
  "Public Limited Company (PLC)",
];

// ── Inline SignaturePad ────────────────────────────────────────────────────────
function SignaturePad({ onCapture }) {
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);
  const lastPos = useRef(null);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
  }, []);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    isDrawing.current = true;
    const pos = getPos(e);
    lastPos.current = pos;
    const ctx = canvasRef.current.getContext("2d");
    ctx.fillStyle = "#032352";
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, 1.5, 0, Math.PI * 2);
    ctx.fill();
    setEmpty(false);
  };

  const move = (e) => {
    if (!isDrawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.strokeStyle = "#032352";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
  };

  const stop = () => {
    if (!isDrawing.current) return;
    isDrawing.current = false;
    onCapture(canvasRef.current.toDataURL("image/png"));
  };

  const clear = (e) => {
    e.stopPropagation();
    const canvas = canvasRef.current;
    const dpr = window.devicePixelRatio || 1;
    canvas.getContext("2d").clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    setEmpty(true);
    onCapture(null);
  };

  return (
    <div className="relative border-2 border-[#032352] rounded-lg bg-white" style={{ height: 140 }}>
      <canvas
        ref={canvasRef}
        data-testid="account-form-signature-canvas"
        className="w-full h-full rounded-lg"
        style={{ display: "block", touchAction: "none", cursor: "crosshair" }}
        onMouseDown={start} onMouseMove={move} onMouseUp={stop} onMouseLeave={stop}
        onTouchStart={start} onTouchMove={move} onTouchEnd={stop}
      />
      {empty && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="text-slate-400 text-sm select-none">Draw your signature here</span>
        </div>
      )}
      {!empty && (
        <button type="button" onClick={clear} data-testid="account-form-clear-signature-btn"
          className="absolute top-2 right-2 text-xs bg-white border border-slate-200 rounded px-2 py-1 text-slate-500 hover:text-red-500 transition-colors z-10">
          Clear
        </button>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-100 bg-[#032352]">
        <p className="text-xs font-semibold text-white uppercase tracking-wider">{title}</p>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, id, required, children }) {
  return (
    <div>
      <Label htmlFor={id} className="text-xs uppercase tracking-wider text-[#5B5C5F] font-semibold mb-1 block">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      {children}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function AccountForm() {
  const location = useLocation();
  const token = location.pathname.split("/account-form/")[1] || "";

  const [pageState, setPageState] = useState("loading");
  const [formData, setFormData] = useState(null);

  const [tradingName, setTradingName] = useState("");
  const [regNumber, setRegNumber] = useState("");
  const [tradingStyle, setTradingStyle] = useState("");

  // Address — separate fields
  const [addrLine1, setAddrLine1] = useState("");
  const [addrLine2, setAddrLine2] = useState("");
  const [town, setTown] = useState("");
  const [county, setCounty] = useState("");
  const [postcode, setPostcode] = useState("");

  const [acctName, setAcctName] = useState("");
  const [acctEmail, setAcctEmail] = useState("");
  const [acctPhone, setAcctPhone] = useState("");
  const [buyerName, setBuyerName] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [buyerPhone, setBuyerPhone] = useState("");

  // Signatory
  const [signatoryName, setSignatoryName] = useState("");
  const [signature, setSignature] = useState(null);

  const [tcAgreed, setTcAgreed] = useState(false);
  const [tcScrolled, setTcScrolled] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const tcRef = useRef(null);

  useEffect(() => {
    if (!token) { setPageState("error"); return; }
    api.get(`/accounts/form/${token}`)
      .then(({ data }) => {
        setFormData(data);
        if (data.already_submitted) {
          setPageState("already_submitted");
        } else {
          setTradingName(data.trading_name || "");
          setRegNumber(data.registration_number || "");
          setTradingStyle(data.trading_style || "");
          setAcctName(data.accounts_contact_name || "");
          setAcctEmail(data.accounts_contact_email || "");
          setAcctPhone(data.accounts_contact_phone || "");
          setBuyerName(data.buyer_contact_name || "");
          setBuyerEmail(data.buyer_contact_email || "");
          setBuyerPhone(data.buyer_contact_phone || "");
          setPageState("form");
        }
      })
      .catch(() => setPageState("error"));
  }, [token]);

  const handleTcScroll = () => {
    const el = tcRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) setTcScrolled(true);
  };

  const fullAddress = [addrLine1, addrLine2, town, county, postcode].filter(Boolean).join(", ");

  const canSubmit =
    tradingName.trim() && addrLine1.trim() && postcode.trim() &&
    acctName.trim() && acctEmail.trim() &&
    signatoryName.trim() && signature && tcAgreed && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post(`/accounts/form/${token}/submit`, {
        trading_name: tradingName.trim(),
        registration_number: regNumber.trim(),
        trading_style: tradingStyle,
        registered_address: fullAddress,        accounts_contact_name: acctName.trim(),
        accounts_contact_email: acctEmail.trim(),
        accounts_contact_phone: acctPhone.trim(),
        buyer_contact_name: buyerName.trim(),
        buyer_contact_email: buyerEmail.trim(),
        buyer_contact_phone: buyerPhone.trim(),
        signatory_name: signatoryName.trim(),
        signature_base64: signature,
        tc_agreed: tcAgreed,
      });
      setPageState("success");
    } catch (err) {
      if (err?.response?.status === 409) {
        setPageState("already_submitted");
      } else {
        toast.error(err?.response?.data?.detail || "Failed to submit. Please try again.");
        setSubmitting(false);
      }
    }
  };

  const customerName = formData?.customer_name || "Valued Customer";

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="account-form-page">
      <div className="bg-[#032352] text-white px-4 py-3 flex items-center gap-3 shadow-md">
        <div className="bg-white rounded-md p-1.5 shadow-sm shrink-0">
          <img src={LOGO_URL} alt="J&L Industrial Supplies" className="h-7 object-contain" crossOrigin="anonymous" />
        </div>
        <div className="h-6 w-px bg-white/20" />
        <span className="font-semibold text-sm tracking-wide">Credit Account Application</span>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">

        {pageState === "loading" && (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <Loader2 className="w-8 h-8 animate-spin text-[#032352]" />
            <p className="text-[#5B5C5F]">Loading application form...</p>
          </div>
        )}

        {pageState === "error" && (
          <div className="bg-white border border-red-200 rounded-xl p-8 text-center mt-8">
            <h2 className="font-heading text-xl font-bold text-[#032352] mb-2">Link Not Found</h2>
            <p className="text-[#5B5C5F] text-sm">This application link is invalid or has expired. Please contact J&L Industrial Supplies for a new link.</p>
          </div>
        )}

        {pageState === "already_submitted" && (
          <div className="bg-white border border-emerald-200 rounded-xl p-8 text-center mt-8" data-testid="account-form-already-submitted">
            <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7 text-emerald-500" />
            </div>
            <h2 className="font-heading text-xl font-bold text-[#032352] mb-2">Application Already Submitted</h2>
            <p className="text-[#5B5C5F] text-sm">Your credit account application has been submitted and is under review. You will be contacted once a decision has been made.</p>
          </div>
        )}

        {pageState === "success" && (
          <div className="bg-white border border-emerald-200 rounded-xl p-8 text-center mt-8" data-testid="account-form-success">
            <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7 text-emerald-500" />
            </div>
            <h2 className="font-heading text-xl font-bold text-[#032352] mb-2">Application Submitted!</h2>
            <p className="text-[#5B5C5F] text-sm">Thank you — your credit account application has been received and is under review. We will be in touch shortly.</p>
          </div>
        )}

        {pageState === "form" && (
          <>
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <p className="text-xs uppercase tracking-wider text-[#5B5C5F] mb-1">Credit Account Application</p>
              <p className="font-heading font-bold text-[#032352] text-xl mb-1">Welcome, {customerName}</p>
              <p className="text-[#5B5C5F] text-sm">Please complete the form below to apply for a credit account. Fields marked <span className="text-red-500">*</span> are required.</p>
            </div>

            {/* Company Information */}
            <Section title="Company Information">
              <Field label="Trading Name" id="trading-name" required>
                <Input id="trading-name" data-testid="account-form-trading-name" value={tradingName}
                  onChange={(e) => setTradingName(e.target.value)} placeholder="Legal trading name of your business" className="mt-1" />
              </Field>
              <Field label="Company Registration Number" id="reg-number">
                <Input id="reg-number" data-testid="account-form-reg-number" value={regNumber}
                  onChange={(e) => setRegNumber(e.target.value)} placeholder="e.g. 12345678" className="mt-1" />
              </Field>

              {/* Trading Style — tick boxes */}
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5B5C5F] font-semibold mb-2 block">
                  Trading Style
                </Label>
                <div className="flex flex-wrap gap-2" data-testid="account-form-trading-style-group">
                  {TRADING_STYLES.map((style) => (
                    <button
                      key={style}
                      type="button"
                      data-testid={`trading-style-${style.replace(/[^a-zA-Z]/g, "-").toLowerCase()}`}
                      onClick={() => setTradingStyle(tradingStyle === style ? "" : style)}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                        tradingStyle === style
                          ? "bg-[#032352] text-white border-[#032352] font-semibold"
                          : "bg-white text-[#5B5C5F] border-slate-300 hover:border-[#032352] hover:text-[#032352]"
                      }`}
                    >
                      {tradingStyle === style && <span className="mr-1.5">&#10003;</span>}
                      {style}
                    </button>
                  ))}
                </div>
              </div>
            </Section>

            {/* Registered Address */}
            <Section title="Registered / Trading Address">
              <p className="text-xs text-[#5B5C5F] -mt-2">Please enter your full registered trading address.</p>

              <Field label="Address Line 1" id="addr-line1" required>
                <Input id="addr-line1" data-testid="account-form-addr-line1"
                  value={addrLine1} onChange={(e) => setAddrLine1(e.target.value)}
                  placeholder="Unit 4, 12 High Street" className="mt-1" />
              </Field>

              <Field label="Address Line 2" id="addr-line2">
                <Input id="addr-line2" data-testid="account-form-addr-line2"
                  value={addrLine2} onChange={(e) => setAddrLine2(e.target.value)}
                  placeholder="Industrial Estate (optional)" className="mt-1" />
              </Field>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Town / City" id="town" required>
                  <Input id="town" data-testid="account-form-town"
                    value={town} onChange={(e) => setTown(e.target.value)}
                    placeholder="Birmingham" className="mt-1" />
                </Field>
                <Field label="County" id="county">
                  <Input id="county" data-testid="account-form-county"
                    value={county} onChange={(e) => setCounty(e.target.value)}
                    placeholder="West Midlands" className="mt-1" />
                </Field>
              </div>

              <Field label="Postcode" id="postcode" required>
                <Input id="postcode" data-testid="account-form-postcode"
                  value={postcode} onChange={(e) => setPostcode(e.target.value.toUpperCase())}
                  placeholder="B1 1AA" className="mt-1 font-mono uppercase tracking-widest max-w-xs" />
              </Field>
            </Section>

            {/* Accounts Contact */}
            <Section title="Accounts / Finance Contact">
              <p className="text-xs text-[#5B5C5F] -mt-2">This contact will receive account correspondence and the signed PDF.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Full Name" id="acct-name" required>
                  <Input id="acct-name" data-testid="account-form-acct-name" value={acctName}
                    onChange={(e) => setAcctName(e.target.value)} placeholder="Accounts manager" className="mt-1" />
                </Field>
                <Field label="Email Address" id="acct-email" required>
                  <Input id="acct-email" data-testid="account-form-acct-email" type="email" value={acctEmail}
                    onChange={(e) => setAcctEmail(e.target.value)} placeholder="accounts@yourcompany.co.uk" className="mt-1" />
                </Field>
              </div>
              <Field label="Phone Number" id="acct-phone">
                <Input id="acct-phone" data-testid="account-form-acct-phone" value={acctPhone}
                  onChange={(e) => setAcctPhone(e.target.value)} placeholder="01234 567890" className="mt-1" />
              </Field>
            </Section>

            {/* Buyer Contact */}
            <Section title="Buyer / Purchasing Contact">
              <p className="text-xs text-[#5B5C5F] -mt-2">This contact will also receive a copy of the signed PDF upon approval.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Full Name" id="buyer-name">
                  <Input id="buyer-name" data-testid="account-form-buyer-name" value={buyerName}
                    onChange={(e) => setBuyerName(e.target.value)} placeholder="Purchasing manager" className="mt-1" />
                </Field>
                <Field label="Email Address" id="buyer-email">
                  <Input id="buyer-email" data-testid="account-form-buyer-email" type="email" value={buyerEmail}
                    onChange={(e) => setBuyerEmail(e.target.value)} placeholder="buyer@yourcompany.co.uk" className="mt-1" />
                </Field>
              </div>
              <Field label="Phone Number" id="buyer-phone">
                <Input id="buyer-phone" data-testid="account-form-buyer-phone" value={buyerPhone}
                  onChange={(e) => setBuyerPhone(e.target.value)} placeholder="01234 567890" className="mt-1" />
              </Field>
            </Section>

            {/* Terms & Conditions */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100 bg-[#032352]">
                <p className="text-xs font-semibold text-white uppercase tracking-wider">Terms &amp; Conditions</p>
              </div>
              <div className="p-5">
                <p className="text-xs text-[#5B5C5F] mb-3">Please scroll through and read the full Terms &amp; Conditions before signing.</p>
                <div ref={tcRef} onScroll={handleTcScroll} data-testid="account-form-tc-scroll"
                  className="border border-slate-200 rounded-lg bg-[#F8FAFC] p-4 text-xs text-[#5B5C5F] font-mono leading-relaxed whitespace-pre-wrap"
                  style={{ height: 260, overflowY: "auto" }}>
                  {TERMS_TEXT}
                </div>
                {!tcScrolled && (
                  <p className="text-xs text-amber-600 mt-2">Please scroll to the bottom of the Terms &amp; Conditions to continue.</p>
                )}
                {tcScrolled && (
                  <label className="flex items-start gap-3 mt-4 cursor-pointer" data-testid="account-form-tc-agree-label">
                    <input type="checkbox" data-testid="account-form-tc-checkbox" checked={tcAgreed}
                      onChange={(e) => setTcAgreed(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#032352]" />
                    <span className="text-sm text-[#032352] leading-snug">
                      I have read and agree to the Terms &amp; Conditions of Sale. I confirm all information provided is true and accurate.
                    </span>
                  </label>
                )}
              </div>
            </div>

            {/* Signature */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
              <Field label="Full Name of Authorised Signatory" id="signatory-name" required>
                <Input id="signatory-name" data-testid="account-form-signatory-name" value={signatoryName}
                  onChange={(e) => setSignatoryName(e.target.value)}
                  placeholder="Full legal name of the person signing" className="mt-1" />
                <p className="text-xs text-[#5B5C5F] mt-1">This may be different from the accounts or buyer contact above.</p>
              </Field>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5B5C5F] font-semibold mb-2 block">
                  Signature <span className="text-red-500">*</span>
                </Label>
                <SignaturePad onCapture={setSignature} />
                <p className="text-xs text-[#5B5C5F] mt-2">Sign above to authorise this credit account application on behalf of your company.</p>
              </div>
            </div>

            <Button onClick={handleSubmit} disabled={!canSubmit} data-testid="account-form-submit-btn"
              className="w-full bg-[#032352] hover:bg-[#04347A] disabled:opacity-40 h-12 text-base">
              {submitting
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                : <><CheckCircle2 className="w-4 h-4 mr-2" /> Submit Credit Account Application</>}
            </Button>

            <p className="text-center text-xs text-[#5B5C5F] pb-6">
              By submitting, you confirm you are authorised to apply for credit on behalf of your company.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
